import { useRef, useState } from "react";
import { Plus, X, Upload } from "lucide-react";
import { CoreDrawer } from "@/components/common/CoreDrawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PronunciationVault, PronunciationVaultInput } from "@/types/agent/pronunciation-vault";
import { usePronunciationSuggester } from "@/components/common/PronunciationSuggester";
import { AIChatService } from "@/services/api/AIChatService";
import { mergePronunciations } from "@/lib/pronunciations";

const ACCENTS = [
  "British English",
  "American English",
  "Australian English",
  "New Zealand English",
  "Indian English",
  "Irish English",
  "South African English",
];

interface VaultDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  editingVault: PronunciationVault | null;
  form: PronunciationVaultInput;
  setForm: React.Dispatch<React.SetStateAction<PronunciationVaultInput>>;
  onSave: () => void;
  isSaving: boolean;
}

export const VaultDrawer = ({ isOpen, onClose, editingVault, form, setForm, onSave, isSaving }: VaultDrawerProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  const addPair = () => setForm((f) => ({ ...f, pairs: [...f.pairs, { word: "", pronunciation: "" }] }));

  const removePair = (i: number) =>
    setForm((f) => ({ ...f, pairs: f.pairs.filter((_, idx) => idx !== i) }));

  const updatePair = (i: number, field: "word" | "pronunciation", value: string) =>
    setForm((f) => {
      const pairs = [...f.pairs];
      pairs[i] = { ...pairs[i], [field]: value };
      return { ...f, pairs };
    });

  const { SuggesterPanel, SuggestionBadge } = usePronunciationSuggester(form.pairs, (index, pronunciation) => {
    updatePair(index, "pronunciation", pronunciation);
  }, form.accent || undefined);

  const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!fileInputRef.current) return;
    fileInputRef.current.value = "";
    if (!file) return;

    setIsImporting(true);
    setImportError(null);

    try {
      const fileContent = await file.text();
      const pairs = await AIChatService.importPronunciationsFromFile({
        fileContent,
        accent: form.accent || undefined,
      });
      setForm((f) => ({ ...f, pairs: mergePronunciations(f.pairs, pairs) }));
    } catch {
      setImportError("Failed to extract pronunciations from file. Please try again.");
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <CoreDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={editingVault ? "Edit Vault" : "New Vault"}
      className="sm:!max-w-[60vw]"
    >
      <div className="flex flex-col gap-5 py-2">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-genie-text-primary">Name *</label>
          <Input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g., Tech Industry Terms"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-genie-text-primary">Description</label>
          <Input
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Brief description of this collection"
          />
        </div>

        <div className="flex gap-4">
          <div className="flex-1 flex flex-col gap-1.5">
            <label className="text-sm font-medium text-genie-text-primary">Accent</label>
            <select
              value={form.accent}
              onChange={(e) => setForm((f) => ({ ...f, accent: e.target.value }))}
              className="rounded-md border border-genie-border px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-genie-primary"
            >
              <option value="">No specific accent</option>
              {ACCENTS.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5 justify-end pb-0.5">
            <label className="text-sm font-medium text-genie-text-primary">Visibility</label>
            <div className="flex items-center gap-2 h-9">
              <input
                type="checkbox"
                id="vault_is_public"
                checked={form.is_public}
                onChange={(e) => setForm((f) => ({ ...f, is_public: e.target.checked }))}
                className="w-4 h-4 rounded"
              />
              <label htmlFor="vault_is_public" className="text-sm text-genie-text-secondary">
                Public (visible to all users)
              </label>
            </div>
          </div>
        </div>

        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            id="vault_is_default"
            checked={form.is_default ?? false}
            onChange={(e) => setForm((f) => ({ ...f, is_default: e.target.checked }))}
            className="w-4 h-4 rounded mt-0.5"
          />
          <label htmlFor="vault_is_default" className="text-sm text-genie-text-secondary">
            Default for all genies (added to every new genie, and to existing genies when you apply defaults)
          </label>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-genie-text-primary">Pronunciation Pairs</label>
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.json,.txt,.tsv,.xlsx,.docx,.md"
                className="hidden"
                onChange={handleFileImport}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={isImporting}
                className="gap-1"
              >
                <Upload className="w-3.5 h-3.5" />
                {isImporting ? "Importing..." : "Import file"}
              </Button>
              <Button variant="outline" size="sm" onClick={addPair} className="gap-1">
                <Plus className="w-3.5 h-3.5" />
                Add Pair
              </Button>
            </div>
          </div>

          {importError && (
            <p className="text-xs text-red-500">{importError}</p>
          )}

          {form.pairs.length === 0 ? (
            <div className="text-center py-8 border border-dashed border-genie-border rounded-lg text-sm text-genie-text-muted">
              No pairs yet — click "Add Pair" or import a file to start.
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {form.pairs.map((pair, i) => (
                <div key={i} className="flex flex-col gap-1.5">
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      {i === 0 && <label className="block text-xs text-genie-text-secondary mb-1">Word</label>}
                      <Input
                        value={pair.word}
                        onChange={(e) => updatePair(i, "word", e.target.value)}
                        placeholder="e.g., LinkedIn"
                      />
                    </div>
                    <div className="flex-1">
                      {i === 0 && <label className="block text-xs text-genie-text-secondary mb-1">Pronunciation</label>}
                      <Input
                        value={pair.pronunciation}
                        onChange={(e) => updatePair(i, "pronunciation", e.target.value)}
                        placeholder="e.g., Linked In"
                      />
                    </div>
                    <button
                      onClick={() => removePair(i)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-md border border-red-200 transition-colors mb-0.5"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <SuggestionBadge index={i} />
                </div>
              ))}
            </div>
          )}
        </div>

        {SuggesterPanel}

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={isSaving}
            className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] hover:brightness-105 text-white border-0"
          >
            {isSaving ? "Saving..." : editingVault ? "Save Changes" : "Create Vault"}
          </Button>
        </div>
      </div>
    </CoreDrawer>
  );
};
